import { PageLayout } from './components/PageLayout';
import { Grid, Snackbar, Tooltip, Typography } from '@mui/material';
import Searchbar from '../common/Searchbar';
import React, { useState } from 'react';
import { useHistory } from 'react-router-dom';
import useFetchUserAssets from './hooks/useFetchUserAssets';
import styled from 'styled-components';
import { Body12, Header12, Header16 } from '../common/Typography';
import Link from '@mui/material/Link';
import { AddNewAsset } from './components/AddNewAsset';
import DialogForm from '../common/DialogForm';
import { AssetForm } from './components/AssetForm';
import useEditAsset from './hooks/useEditAsset';
import useUserInfo from '../profile/hooks/useUserInfo';
import FORMAT_SIZES from '../common/constants/FormatSizes';
import { FixBannerPageLayout } from '../common/Mobile/FixBannerPageLayout';
import { THEME_COLORS } from '../common/colors';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import DialogContent from '@mui/material/DialogContent';
import { SuggestedAssetList } from './components/SuggestedAssetList';

const ASSET_CARD_WIDTH = 122;

const AssetCardContainer = styled.div`
  text-align: center;
  width: ${ASSET_CARD_WIDTH}px;

  @media screen and (max-width: 320px) {
    width: 100%;
  }

  @media screen and (min-width: 375px) {
    width: ${ASSET_CARD_WIDTH + 26}px;
  }

  @media screen and (min-width: 425px) {
    width: ${ASSET_CARD_WIDTH + 48}px;
  }

  img {
    cursor: pointer;
  }
`;

const ConfirmModalClose = styled(Snackbar)`
  position: absolute !important;
  bottom: -80px !important;

  .MuiSnackbarContent-root {
    background-color: #f4f5f4 !important;
    box-shadow: unset !important;
    border: unset !important;
    width: 100%;
    font-size: ${({ isMobile }) => (isMobile ? 1.2 : 1)}rem;
  }
`;

const AssetViewCard = styled.div`
  width: ${ASSET_CARD_WIDTH}px;
  height: ${ASSET_CARD_WIDTH + 10}px;

  @media screen and (max-width: 320px) {
    width: 100%;
    height: auto;
  }

  @media screen and (min-width: 375px) {
    width: ${ASSET_CARD_WIDTH + 26}px;
    height: ${ASSET_CARD_WIDTH + 36}px;
  }

  @media screen and (min-width: 425px) {
    width: ${ASSET_CARD_WIDTH + 48}px;
    height: ${ASSET_CARD_WIDTH + 58}px;
  }

  display: flex;
  justify-content: center;
  align-items: center;
  overflow: hidden;

  img {
    flex-shrink: 0;
    width: 100%;
    min-height: 100%;
  }
`;

const ProTip = styled(Typography)`
  color: ${THEME_COLORS.primary} !important;
  font-size: 0.9rem !important;
  margin: 4px 0 1rem 0 !important;
`;

const AssetCardList = styled.div`
  margin-top: 2.2rem;
  display: flex;
  flex-wrap: wrap;
  column-gap: 8%;
  row-gap: ${({ isMobile }) => (isMobile ? '2rem' : '6%')};
`;

const AssetTitleContainer = styled.div`
  text-align: center;
  margin-top: 0.5rem;
  height: 40px;
  position: relative;
`;

const ViewContainer = styled.div`
  min-height: 100vh;
`;

const AssetLink = styled(Link)`
  text-decoration: unset !important;
  font-weight: bold !important;
  color: #4e5152 !important;
`;

const Subheader = styled(Grid)`
  align-items: center;
  justify-content: space-between;
`;

const HeaderContainer = styled.div`
  text-align: ${({ isMobile }) => (isMobile ? 'center' : 'auto')};
`;

const SearchContainer = styled.div`
  ${({ isMobile }) => !isMobile && 'min-width: 300px;'}
  ${({ isMobile }) => isMobile && 'margin-top: 2rem;'}
`;

function TruncateTitle({ children }) {
  if (children.length > 36) {
    return (
      <Tooltip title={children}>
        <Header12>{children.slice(0, 36)}...</Header12>
      </Tooltip>
    );
  }

  return <Header12>{children}</Header12>;
}

export function ViewUserAssetPage({ format, match, userInfo }) {
  const isMobile = format === FORMAT_SIZES.MOBILE;
  const userId = match.params.id;
  const history = useHistory();
  const { userAssets, fetchAssets } = useFetchUserAssets(
    userId ? { uuid: userId } : userInfo,
  );
  const { userInfo: user } = useUserInfo(userId);
  const [openModal, setOpenModal] = useState(null);
  const [assetId, setAssetId] = useState(null);
  const currentUserAssets =
    window.location.pathname === '/my-offerings' ||
    (userInfo && userId === userInfo.id?.toString());
  const Component = isMobile ? FixBannerPageLayout : PageLayout;
  const openAssetForm = id => {
    setAssetId(id);
    setOpenModal('ASSET_FORM');
  };
  const [confirmAssetDeletion, setConfirmAssetDeletion] = useState(null);

  const onClose = () => {
    setOpenModal(null);
    setAssetId(null);
  };

  const {
    deleteAsset,
    updateAsset,
    createAsset,
    loading: updating,
    errors,
  } = useEditAsset(onClose, fetchAssets);

  if (window.location.pathname !== '/my-offerings' && currentUserAssets) {
    history.push('/my-offerings');
  }

  const handleAfterSave = () => {
    setOpenModal('SUGGESTED_ASSETS');
  };

  return (
    <Component>
      <ViewContainer>
        <HeaderContainer isMobile={isMobile}>
          <Header16>
            {currentUserAssets
              ? 'My Offerings'
              : `${user.firstname}'s Offerings`}
          </Header16>

          <Subheader container>
            <Grid item xs={12} sm={12} lg={6}>
              <Body12 sx={{ margin: { md: '0.5rem 0 !important' } }}>
                I would like to enter a suap by offering...
              </Body12>
              {currentUserAssets && (
                <ProTip>
                  Pro tip: Add 3 offers or more for better matching.
                </ProTip>
              )}
            </Grid>
            <Grid item xs={12} sm={12} lg={6}>
              <SearchContainer isMobile={isMobile}>
                <Searchbar onSearch={fetchAssets} />
              </SearchContainer>
            </Grid>
          </Subheader>
        </HeaderContainer>
        <AssetCardList isMobile={isMobile}>
          {currentUserAssets && (
            <AssetCardContainer style={{ marginLeft: 0 }}>
              <AddNewAsset
                size={isMobile ? 'mobile' : 'md'}
                openAssetForm={() => openAssetForm(null)}
              />
              <AssetTitleContainer>
                <TruncateTitle>Add new offer</TruncateTitle>
              </AssetTitleContainer>
            </AssetCardContainer>
          )}
          {userAssets.map((asset, i) => {
            return (
              <AssetCardContainer key={`asset-list-${asset.category}-${i}`}>
                <AssetViewCard>
                  <img
                    onClick={() => history.push(`/assets/${asset.uuid}`)}
                    alt={asset.title}
                    src={asset.mainImage}
                  />
                </AssetViewCard>
                <AssetTitleContainer>
                  <TruncateTitle>{asset.title}</TruncateTitle>
                  {currentUserAssets && (
                    <div>
                      <ProTip
                        onClick={() =>
                          setConfirmAssetDeletion(
                            `asset-list-${asset.category}-${i}`,
                          )
                        }
                      >
                        Delete
                      </ProTip>
                      <ConfirmModalClose
                        isMobile={isMobile}
                        open={
                          confirmAssetDeletion ===
                          `asset-list-${asset.category}-${i}`
                        }
                        message="Are you sure you want to delete?"
                        action={
                          <div
                            style={{
                              margin: 0,
                              width: '100%',
                              display: 'flex',
                              justifyContent: 'space-between',
                            }}
                          >
                            <div onClick={() => setConfirmAssetDeletion(null)}>
                              Cancel
                            </div>
                            <div
                              style={{ marginLeft: isMobile ? '2rem' : '1rem' }}
                              onClick={() => deleteAsset(asset.id)}
                            >
                              Yes
                            </div>
                          </div>
                        }
                      />
                    </div>
                  )}
                </AssetTitleContainer>
              </AssetCardContainer>
            );
          })}
        </AssetCardList>
        {openModal === 'ASSET_FORM' && (
          <DialogForm
            label=""
            description=""
            onClose={onClose}
            closePosition="right"
            isMobile={isMobile}
          >
            <AssetForm
              onClose={onClose}
              afterSave={handleAfterSave}
              updateAsset={updateAsset}
              createAsset={createAsset}
              errors={errors}
              updating={updating}
              uuid={assetId}
              isMobile={isMobile}
            />
          </DialogForm>
        )}
        {openModal === 'SUGGESTED_ASSETS' && (
          <DialogForm
            label="Findings for you"
            description=""
            isOpen={true}
            isMobile={isMobile}
            onClose={() => setOpenModal(null)}
          >
            <DialogContent>
              <SuggestedAssetList
                increment={6}
                userUuid={userInfo.uuid}
                isMobile={isMobile}
              />
            </DialogContent>
          </DialogForm>
        )}
      </ViewContainer>
    </Component>
  );
}
